<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch, Ref } from 'vue'
import moment from 'moment'
import Api from '@/services/api/ApiServiceFabrick'
import { useAuth } from '@/services/user/Auth0UserProvider'
import i18n from '@/lang/i18n'
import { ALL_DEVICE_TYPE, TELTONIKA_MAKER_ID } from '@/services/consts'
import { PROVISIONING_CATEGORIES, getProvisioningCategory, isProvisioningRunning } from '@/services/functions/provisioningStatus'
import BulkProvisioningConfirmModal from '@/components/DeviceListParts/BulkProvisioningConfirmModal.vue'
import ManagementOfficeAutoComplete from '@/components/AutoCompletes/ManagementOfficeAutoComplete.vue'
import MultipleOperationOfficeAutoComplete from '@/components/AutoCompletes/MultipleOperationOfficeAutoComplete.vue'
import SingleDeviceTypeAutoComplete from '@/components/AutoCompletes/SingleDeviceTypeAutoComplete.vue'
import PaginateWithCount from '@/components/PaginateWithCount.vue'
import getProvisioningStatusesRequest, { ProvisioningCategory } from '@/types/requests/getProvisioningStatuses'
import executeProvisioningRequest from '@/types/requests/executeProvisioning'
import { ProvisioningDevice, ProvisioningStatus } from '@/types/responses/getProvisioningStatuses'
import getManagementOffice from '@/types/responses/getManagementOffice'
import getOperationOffice from '@/types/responses/getOperationOffice'
import deviceType from '@/types/DeviceType'

const props = defineProps<{
  showFlash: (message: string, type: string, fadeout: boolean) => { value: any }
}>()

const { loginWithRedirect } = useAuth()

document.title = i18n.global.t('pages.gpsTrackerProvisioning.title')

const CATEGORY_COLORS: Record<ProvisioningCategory, string> = {
  not_executed: '#9e9e9e',
  running: '#1e88e5',
  failed: '#e53935',
  done: '#43a047',
}
// topic_pending はカテゴリ上は進行中。フィルタと並び順はサーバーに合わせ、チップだけ別表示にする
const TOPIC_PENDING_COLOR = '#f9a825'

// 強制再実行は証明書を作り直すので危険寄りの色にする
const FORCE_RERUN_COLOR = 'red-darken-2'

// ---- 一覧 ----
const devices: Ref<ProvisioningDevice[]> = ref([])
const currentPage = ref(1)
const totalPage = ref(0)
const listLoading = ref(false)

// ---- 右サイド検索フィルタ ----
// 管理会社はAPIに送らない。運営会社の候補を絞るためだけに使う
type FilterForm = {
  managementOffice: getManagementOffice | undefined,
  operationOffices: getOperationOffice[],
  model_number: string | null,
  fw_version: string | null,
  sim_id: string | null,
  imei: string | null,
  model_type: deviceType | undefined,
  number_plate: string | null,
  status: 'only-active' | 'all',
  categories: ProvisioningCategory[],
}

function createEmptyForm (): FilterForm {
  return {
    managementOffice: undefined,
    operationOffices: [],
    model_number: null,
    fw_version: null,
    sim_id: null,
    imei: null,
    model_type: undefined,
    number_plate: null,
    status: 'only-active',
    categories: [],
  }
}

const form = reactive<FilterForm>(createEmptyForm())

// 車番の候補。車両オートコンプリートAPIから取る
const numberPlateOptions: Ref<string[]> = ref([])

function loadNumberPlateOptions () {
  Api().autoComplete.vehicles().then((vehicles) => {
    const plates = vehicles
      .map((vehicle) => vehicle.number_plate)
      .filter((plate): plate is string => !!plate)
    numberPlateOptions.value = Array.from(new Set(plates)).sort()
  }).catch((error) => {
    handleError(error, 'fetchFailed')
  })
}

// autoCompleteOperationOffices が返すのは managementOfficeId を持つ形。型定義が実データと合っていないので足しておく
type OperationOfficeOption = getOperationOffice & {
  managementOfficeId?: number,
  managementOfficeType?: string,
}
const operationOfficeOptions: Ref<OperationOfficeOption[]> = ref([])

// 一覧APIに管理会社の条件が無いので、管理会社だけ選ばれた時はその配下の運営会社IDに展開して送る
function resolveOperationOfficeIds (): number[] {
  if (form.operationOffices.length > 0) {
    return form.operationOffices.map((office) => office.id)
  }
  if (!form.managementOffice) {
    return []
  }
  const ids = operationOfficeOptions.value
    .filter((office) =>
      office.managementOfficeId === form.managementOffice?.id &&
      office.managementOfficeType === form.managementOffice?.type
    )
    .map((office) => office.id)
  // 配下の運営会社が無い管理会社を選んだ時に全件出てしまわないよう、存在しないIDを送って0件にする
  return ids.length > 0 ? ids : [0]
}

function buildRequest (page: number): getProvisioningStatusesRequest {
  return {
    operation_office_ids: resolveOperationOfficeIds(),
    model_number: form.model_number || null,
    fw_version: form.fw_version || null,
    sim_id: form.sim_id || null,
    imei: form.imei || null,
    model_type: form.model_type?.id ?? null,
    number_plate: form.number_plate || null,
    only_active: form.status === 'only-active',
    provisioning_categories: [...form.categories],
    page,
  }
}

// ページ送りではフォームの現在値ではなく検索した時の条件を使うので持っておく
const searchParams: Ref<getProvisioningStatusesRequest> = ref(buildRequest(1))

function fetchProvisioningStatuses (request: getProvisioningStatusesRequest): Promise<boolean> {
  listLoading.value = true
  selectedIds.value = []
  return Api().getProvisioningStatuses(request).then((data) => {
    devices.value = data.gps_tracker_provisioning_statuses
    currentPage.value = data.current_page
    totalPage.value = data.total_page
    return true
  }).catch((error) => {
    handleError(error, 'fetchFailed')
    return false
  }).finally(() => {
    listLoading.value = false
  })
}

function search (): Promise<boolean> {
  searchParams.value = buildRequest(1)
  return fetchProvisioningStatuses(searchParams.value)
}

function clearFilter () {
  Object.assign(form, createEmptyForm())
  search()
}

onMounted(() => {
  Api().autoCompleteOperationOffices().then((data) => {
    operationOfficeOptions.value = data as OperationOfficeOption[]
  }).catch((error) => {
    handleError(error, 'fetchFailed')
  })
  loadNumberPlateOptions()
  search().then((succeeded) => {
    if (succeeded) {
      loadBulkTotals()
    }
  })
})

watch(currentPage, (page) => {
  if (page === searchParams.value.page) {
    return
  }
  searchParams.value = { ...searchParams.value, page }
  fetchProvisioningStatuses(searchParams.value)
})

// ---- 表示用ヘルパー ----
function categoryOf (device: ProvisioningDevice): ProvisioningCategory {
  return getProvisioningCategory(device.provisioning_status)
}

function categoryLabel (category: ProvisioningCategory): string {
  return i18n.global.t(`pages.gpsTrackerProvisioning.categories.${category}`)
}

// 翻訳キーが無い status はそのまま表示する
function failureReasonLabel (device: ProvisioningDevice): string {
  if (device.provisioning_status === null) {
    return ''
  }
  const key = `pages.gpsTrackerProvisioning.failureReasons.${device.provisioning_status.status}`
  return i18n.global.te(key) ? i18n.global.t(key) : device.provisioning_status.status
}

function isTopicPending (device: ProvisioningDevice): boolean {
  return device.provisioning_status?.status === 'topic_pending'
}

function isRunning (device: ProvisioningDevice): boolean {
  return isProvisioningRunning(device.provisioning_status)
}

// 通常実行(force=false)のボタン。送っても必ずスキップされる実行中と完了には出さない
type NormalAction = 'execute' | 'retry' | null

function normalAction (device: ProvisioningDevice): NormalAction {
  if (isRunning(device)) {
    return null
  }
  if (isTopicPending(device)) {
    return 'retry'
  }
  const category = categoryOf(device)
  if (category === 'not_executed') {
    return 'execute'
  }
  if (category === 'done') {
    return null
  }
  return 'retry'
}

// 通常実行ではスキップされる端末。完了と実行中
function isSkippedByNormalRun (device: ProvisioningDevice): boolean {
  return isRunning(device) || categoryOf(device) === 'done'
}

function normalActionLabel (device: ProvisioningDevice): string {
  const action = normalAction(device)
  if (action === 'execute') {
    return i18n.global.t('pages.gpsTrackerProvisioning.buttons.execute')
  }
  return i18n.global.t('pages.gpsTrackerProvisioning.buttons.retry')
}

function statusChipLabel (device: ProvisioningDevice): string {
  if (isTopicPending(device)) {
    return i18n.global.t('pages.gpsTrackerProvisioning.statusChips.topic_pending')
  }
  return categoryLabel(categoryOf(device))
}

function statusChipColor (device: ProvisioningDevice): string {
  return isTopicPending(device) ? TOPIC_PENDING_COLOR : CATEGORY_COLORS[categoryOf(device)]
}

function modelTypeLabel (modelType: number | null): string {
  return ALL_DEVICE_TYPE.find((type) => type.id === modelType)?.type ?? ''
}

function formatDateTime (value: string | null | undefined): string {
  if (!value) {
    return '—'
  }
  return moment(value).format('YYYY-MM-DD HH:mm')
}

// ---- 状態更新。表示中デバイスの状態だけ取り直す ----
const refreshDone = ref(false)
const refreshDoneTime = ref('')
let refreshDoneTimer: ReturnType<typeof setTimeout> | null = null

function refreshStatus () {
  if (listLoading.value || devices.value.length === 0) {
    return
  }
  listLoading.value = true
  const deviceIds = devices.value.map((device) => device.id)
  Api().refreshProvisioningStatuses(deviceIds).then((data) => {
    const refreshed = new Map<number, ProvisioningStatus | null>(
      data.gps_tracker_provisioning_statuses.map((item) => [item.id, item.provisioning_status])
    )
    devices.value.forEach((device) => {
      if (refreshed.has(device.id)) {
        device.provisioning_status = refreshed.get(device.id) ?? null
      }
    })
    refreshDoneTime.value = moment().format('HH:mm:ss')
    refreshDone.value = true
    if (refreshDoneTimer) {
      clearTimeout(refreshDoneTimer)
    }
    refreshDoneTimer = setTimeout(() => {
      refreshDone.value = false
    }, 10000)
  }).catch((error) => {
    handleError(error, 'fetchFailed')
  }).finally(() => {
    listLoading.value = false
  })
}

// ---- 一括実行の対象。絞り込みに関係なく有効デバイス全件 ----
// 件数はカテゴリ指定した一覧APIの total から取る。実行対象のIDは一括ボタンを押した時に全ページ辿って集める
type BulkCategory = 'not_executed' | 'failed'
const bulkTotals = reactive<Record<BulkCategory, number>>({ not_executed: 0, failed: 0 })
const bulkTargetsLoading = ref(false)

function buildBulkRequest (category: BulkCategory, page: number): getProvisioningStatusesRequest {
  return {
    operation_office_ids: [],
    model_number: null,
    fw_version: null,
    sim_id: null,
    imei: null,
    model_type: null,
    number_plate: null,
    only_active: true,
    provisioning_categories: [category],
    page,
  }
}

function loadBulkTotals () {
  Promise.all([
    Api().getProvisioningStatuses(buildBulkRequest('not_executed', 1)),
    Api().getProvisioningStatuses(buildBulkRequest('failed', 1)),
  ]).then(([notExecuted, failed]) => {
    bulkTotals.not_executed = notExecuted.total
    bulkTotals.failed = failed.total
  }).catch((error) => {
    handleError(error, 'fetchFailed')
  })
}

async function fetchAllDeviceIdsByCategory (category: BulkCategory): Promise<number[]> {
  const ids: number[] = []
  let page = 1
  let lastPage = 1
  do {
    const data = await Api().getProvisioningStatuses(buildBulkRequest(category, page))
    ids.push(...data.gps_tracker_provisioning_statuses.map((device) => device.id))
    lastPage = data.total_page
    page += 1
  } while (page <= lastPage)
  return ids
}

// ---- 選択。強制再実行の対象になるので全状態で選べる ----
const selectedIds: Ref<number[]> = ref([])
const selectableDeviceIds = computed(() => devices.value.map((device) => device.id))
const allSelected = computed(() =>
  selectableDeviceIds.value.length > 0 && selectableDeviceIds.value.every((id) => selectedIds.value.includes(id))
)

function toggleAll (event: Event) {
  const checked = (event.target as HTMLInputElement).checked
  selectedIds.value = checked ? [...selectableDeviceIds.value] : []
}

// ---- 実行確認 ----
const showModal = ref(false)
const executing = ref(false)
const pendingIds: Ref<number[]> = ref([])
const pendingCategory: Ref<BulkCategory | null> = ref(null)
const pendingForce = ref(false)
const pendingCategoryLabel = computed(() => pendingCategory.value ? categoryLabel(pendingCategory.value) : null)

// 対象のうち通常実行でスキップされる台数と、実行中の台数。モーダルの案内に使う
const pendingDevices = computed(() => {
  const pendingIdSet = new Set(pendingIds.value)
  return devices.value.filter((device) => pendingIdSet.has(device.id))
})
const pendingSkipCount = computed(() => pendingDevices.value.filter(isSkippedByNormalRun).length)
const pendingRunningCount = computed(() => pendingDevices.value.filter(isRunning).length)

function openModalForIds (ids: number[], force: boolean) {
  if (ids.length === 0) {
    props.showFlash(i18n.global.t('pages.gpsTrackerProvisioning.messages.selectDevice'), 'warn', false)
    return
  }
  pendingIds.value = ids
  pendingCategory.value = null
  pendingForce.value = force
  showModal.value = true
}

function openModalForCategory (category: BulkCategory) {
  if (bulkTargetsLoading.value) {
    return
  }
  bulkTargetsLoading.value = true
  fetchAllDeviceIdsByCategory(category).then((ids) => {
    bulkTotals[category] = ids.length
    if (ids.length === 0) {
      props.showFlash(i18n.global.t('pages.gpsTrackerProvisioning.messages.noBulkTargets'), 'warn', false)
      return
    }
    pendingIds.value = ids
    pendingCategory.value = category
    pendingForce.value = false
    showModal.value = true
  }).catch((error) => {
    handleError(error, 'fetchFailed')
  }).finally(() => {
    bulkTargetsLoading.value = false
  })
}

function openSelected () {
  openModalForIds([...selectedIds.value], false)
}

function openForceSelected () {
  openModalForIds([...selectedIds.value], true)
}

function openRow (device: ProvisioningDevice) {
  openModalForIds([device.id], false)
}

function openForceRow (device: ProvisioningDevice) {
  openModalForIds([device.id], true)
}

function openBulkNotExecuted () {
  openModalForCategory('not_executed')
}

function openRetryFailed () {
  openModalForCategory('failed')
}

// force は端末の状態ではなく押したボタンで決まる。実行APIの上限に合わせて50件ずつ送る
const EXECUTE_CHUNK_SIZE = 50

function chunkExecuteRequests (deviceIds: number[], force: boolean): executeProvisioningRequest[] {
  const requests: executeProvisioningRequest[] = []
  for (let index = 0; index < deviceIds.length; index += EXECUTE_CHUNK_SIZE) {
    requests.push({ device_ids: deviceIds.slice(index, index + EXECUTE_CHUNK_SIZE), force })
  }
  return requests
}

function buildExecuteRequests (): executeProvisioningRequest[] {
  return chunkExecuteRequests(pendingIds.value, pendingForce.value)
}

function execute () {
  showModal.value = false
  const requests = buildExecuteRequests()
  executing.value = true
  Promise.all(requests.map((request) => Api().executeProvisioning(request))).then((responses) => {
    const results = responses.flatMap((response) => response.results)
    const skippedCount = results.filter((result) => result.action === 'skipped').length
    const failedCount = results.filter((result) => result.action === 'failed').length
    // skipped は完了か実行中。失敗ではないのでそのまま案内する
    const messages = [i18n.global.t('pages.gpsTrackerProvisioning.messages.executed')]
    if (skippedCount > 0) {
      messages.push(i18n.global.t('pages.gpsTrackerProvisioning.messages.executeSkipped', { count: skippedCount }))
    }
    if (failedCount > 0) {
      messages.push(i18n.global.t('pages.gpsTrackerProvisioning.messages.executePartiallyFailed', { count: failedCount }))
    }
    props.showFlash(messages.join(' '), skippedCount > 0 || failedCount > 0 ? 'warn' : 'info', false)
    selectedIds.value = []
    refreshStatus()
    loadBulkTotals()
  }).catch((error) => {
    handleError(error, 'executeFailed')
  }).finally(() => {
    executing.value = false
    pendingIds.value = []
    pendingCategory.value = null
    pendingForce.value = false
  })
}

// ---- エラー ----
function handleError (error: any, messageKey: 'fetchFailed' | 'executeFailed') {
  const status = error?.response?.status
  if (status === 401) {
    loginWithRedirect({ appState: { targetUrl: location.pathname } })
    return
  }
  if (status === 403) {
    props.showFlash(i18n.global.t('pages.common.messages.forbidden'), 'error', false)
    return
  }
  // 422 は errors に項目ごとのメッセージが入る。実行APIの事前検証もここに来るので中身を見せる
  if (status === 422) {
    const errors = (error?.response?.data?.errors ?? {}) as Record<string, string[]>
    const validationMessages = Object.values(errors).flat().filter((message) => typeof message === 'string')
    if (validationMessages.length > 0) {
      props.showFlash(validationMessages.join(' '), 'error', false)
      return
    }
  }
  props.showFlash(i18n.global.t(`pages.gpsTrackerProvisioning.messages.${messageKey}`), 'error', false)
}
</script>

<template>
  <div class="d-flex" style="min-height: 45vw;">
    <div class="content p-4 w-100">
      <div class="d-flex align-center mb-4">
        <h4 class="card-title mt-0">{{ i18n.global.t('pages.gpsTrackerProvisioning.title') }}</h4>
      </div>

      <div>
        <!-- プロビジョニング操作と案内を同じ行に置く。デバイス登録と編集はこの画面に含めない -->
        <div class="d-flex align-center justify-space-between flex-wrap mb-1">
          <div class="d-flex align-center flex-wrap">
            <v-btn
              v-if="selectedIds.length"
              class="mt-0 mr-2 mb-1"
              color="#15829C"
              :disabled="executing"
              @click="openSelected"
            >
              {{ i18n.global.t('pages.gpsTrackerProvisioning.buttons.executeSelected', { count: selectedIds.length }) }}
            </v-btn>
            <v-btn
              v-if="selectedIds.length"
              class="mt-0 mr-2 mb-1"
              :color="FORCE_RERUN_COLOR"
              :disabled="executing"
              @click="openForceSelected"
            >
              {{ i18n.global.t('pages.gpsTrackerProvisioning.buttons.forceRerunSelected', { count: selectedIds.length }) }}
            </v-btn>
            <v-btn
              class="mt-0 mr-2 mb-1"
              color="orange-darken-3"
              :disabled="executing"
              :loading="bulkTargetsLoading && pendingCategory === 'not_executed'"
              @click="openBulkNotExecuted"
            >
              {{ i18n.global.t('pages.gpsTrackerProvisioning.buttons.executeAllNotExecuted', { count: bulkTotals.not_executed }) }}
            </v-btn>
            <v-btn
              class="mt-0 mr-1 mb-1"
              color="blue-grey-darken-1"
              :disabled="executing"
              :loading="bulkTargetsLoading && pendingCategory === 'failed'"
              @click="openRetryFailed"
            >
              {{ i18n.global.t('pages.gpsTrackerProvisioning.buttons.retryAllFailed', { count: bulkTotals.failed }) }}
            </v-btn>
            <v-tooltip
              location="bottom"
              open-on-click
              max-width="360"
            >
              <template #activator="{ props: tooltipProps }">
                <v-icon
                  v-bind="tooltipProps"
                  icon="mdi-information-outline"
                  size="small"
                  color="grey-darken-1"
                  class="mb-1"
                  style="cursor: pointer;"
                />
              </template>
              {{ i18n.global.t('pages.gpsTrackerProvisioning.tooltips.bulkScope') }}
            </v-tooltip>
          </div>
          <div class="d-flex align-center flex-wrap">
            <v-btn
              :class="['refresh-btn', 'mr-3', 'mb-1', { 'refresh-btn--done': refreshDone }]"
              variant="outlined"
              :color="refreshDone ? 'success' : '#15829C'"
              size="small"
              :prepend-icon="refreshDone ? 'mdi-check-circle' : 'mdi-refresh'"
              :loading="listLoading"
              :disabled="listLoading || refreshDone || devices.length === 0"
              @click="refreshStatus"
            >
              {{
                refreshDone
                  ? i18n.global.t('pages.gpsTrackerProvisioning.buttons.refreshDone', { time: refreshDoneTime })
                  : i18n.global.t('pages.gpsTrackerProvisioning.buttons.refreshStatus')
              }}
            </v-btn>
            <div class="d-flex align-center notice-label">
              <v-icon color="warning" size="small" class="mr-1">mdi-alert-circle-outline</v-icon>
              <span>
                {{ i18n.global.t('pages.gpsTrackerProvisioning.notice.deviceManagementPrefix') }}
                <router-link :to="{ name: 'DeviceList' }" class="device-list-link">
                  {{ i18n.global.t('pages.gpsTrackerProvisioning.notice.deviceList') }}
                </router-link>
                {{ i18n.global.t('pages.gpsTrackerProvisioning.notice.deviceManagementSuffix') }}
              </span>
            </div>
          </div>
        </div>
        <div class="list list-scrollbar prov-list">
          <div v-if="listLoading" class="prov-loading">
            <v-progress-circular indeterminate color="#15829C" size="48" width="4" />
          </div>
          <v-table>
            <thead>
              <tr>
                <th class="checkbox-column">
                  <input type="checkbox" :checked="allSelected" :disabled="selectableDeviceIds.length === 0" @change="toggleAll" />
                </th>
                <th>{{ i18n.global.t('pages.devices.id') }}</th>
                <th>{{ i18n.global.t('pages.gpsTrackerProvisioning.columns.provisioningStatus') }}</th>
                <th class="text-center">{{ i18n.global.t('pages.gpsTrackerProvisioning.columns.failureReason') }}</th>
                <th>{{ i18n.global.t('pages.gpsTrackerProvisioning.columns.lastExecutedAt') }}</th>
                <th>{{ i18n.global.t('pages.devices.modelNumber') }}</th>
                <th>{{ i18n.global.t('pages.devices.deviceId') }}</th>
                <th>{{ i18n.global.t('pages.devices.simId') }}</th>
                <th>{{ i18n.global.t('pages.devices.imei') }}</th>
                <th>{{ i18n.global.t('pages.devices.modelType') }}</th>
                <th>{{ i18n.global.t('pages.devices.fw') }}</th>
                <th>{{ i18n.global.t('pages.devices.managementOfficeName') }}</th>
                <th>{{ i18n.global.t('pages.devices.operationOfficeName') }}</th>
                <th>{{ i18n.global.t('pages.devices.installationCar') }}</th>
                <th>{{ i18n.global.t('pages.gpsTrackerProvisioning.columns.executedBy') }}</th>
                <th class="text-center action-column">{{ i18n.global.t('pages.gpsTrackerProvisioning.columns.actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!listLoading && devices.length === 0">
                <td colspan="16" class="text-center text-muted">
                  {{ i18n.global.t('pages.gpsTrackerProvisioning.messages.noData') }}
                </td>
              </tr>
              <tr v-for="device in devices" :key="device.id">
                <td class="checkbox-column">
                  <input
                    type="checkbox"
                    :value="device.id"
                    v-model="selectedIds"
                  />
                </td>
                <td>{{ device.id }}</td>
                <td>
                  <v-tooltip v-if="isTopicPending(device)" location="bottom" max-width="320">
                    <template #activator="{ props: tooltipProps }">
                      <span v-bind="tooltipProps" class="status-chip" :style="{ backgroundColor: statusChipColor(device), cursor: 'help' }">
                        {{ statusChipLabel(device) }}
                      </span>
                    </template>
                    {{ i18n.global.t('pages.gpsTrackerProvisioning.tooltips.topicPending') }}
                  </v-tooltip>
                  <span v-else class="status-chip" :style="{ backgroundColor: statusChipColor(device) }">
                    {{ statusChipLabel(device) }}
                  </span>
                </td>
                <td class="text-center">
                  <span v-if="categoryOf(device) === 'failed'" class="text-red-darken-1">
                    {{ failureReasonLabel(device) }}
                  </span>
                  <span v-else-if="categoryOf(device) === 'done'" class="text-muted">—</span>
                </td>
                <td>{{ formatDateTime(device.provisioning_status?.updated_at) }}</td>
                <td>{{ device.model_number }}</td>
                <td>{{ device.device_id }}</td>
                <td>{{ device.sim_id }}</td>
                <td>{{ device.imei ?? '' }}</td>
                <td>{{ modelTypeLabel(device.model_type) }}</td>
                <td>{{ device.firmware_version ?? '' }}</td>
                <td>{{ device.management_office_name ?? '' }}</td>
                <td>{{ device.operation_office_name ?? '' }}</td>
                <td>{{ device.installation_vehicle_number_plate ?? '' }}</td>
                <td>{{ device.provisioning_status?.account_name ?? '' }}</td>
                <td class="text-center action-column">
                  <!-- ボタンを並べると列が広くなるのでドロップダウンにまとめる -->
                  <v-menu>
                    <template #activator="{ props: menuProps }">
                      <v-btn
                        v-bind="menuProps"
                        class="action-menu-btn"
                        variant="outlined"
                        color="#15829C"
                        append-icon="mdi-chevron-down"
                        :disabled="executing"
                      >
                        {{ i18n.global.t('pages.gpsTrackerProvisioning.columns.actions') }}
                      </v-btn>
                    </template>
                    <v-list>
                      <v-list-item
                        v-if="normalAction(device)"
                        @click="openRow(device)"
                      >
                        {{ normalActionLabel(device) }}
                      </v-list-item>
                      <v-list-item @click="openForceRow(device)">
                        <span class="force-rerun-item">
                          {{ i18n.global.t('pages.gpsTrackerProvisioning.buttons.forceRerun') }}
                        </span>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </td>
              </tr>
            </tbody>
          </v-table>
        </div>
        <PaginateWithCount
          class="justify-end"
          :totalPage="totalPage"
          v-model="currentPage"
          :showItemCount="false"
        />
        <hr />
      </div>
    </div>

    <!-- この画面専用の検索フィルタ。デバイス検索と同じ項目にプロビジョニング状態を足す -->
    <div class="filter-panel p-4">
      <h4 class="mt-0">{{ i18n.global.t('pages.common.filter.title') }}</h4>
      <div class="d-grid gap-2">
        <ManagementOfficeAutoComplete
          v-model="form.managementOffice"
          selectFirstIfOnlyOne
        />
        <MultipleOperationOfficeAutoComplete
          v-model="form.operationOffices"
          :managementOffice="form.managementOffice"
        />
        <v-text-field
          :label="i18n.global.t('pages.devices.modelNumber')"
          v-model="form.model_number"
        />
        <v-text-field
          :label="i18n.global.t('pages.devices.fwVersion')"
          v-model="form.fw_version"
        />
        <v-text-field
          :label="i18n.global.t('pages.devices.simId')"
          v-model="form.sim_id"
        />
        <v-text-field
          :label="i18n.global.t('pages.devices.imei')"
          v-model="form.imei"
        />
        <SingleDeviceTypeAutoComplete
          v-model="form.model_type"
          :makerId="TELTONIKA_MAKER_ID"
        />
        <v-autocomplete
          :label="i18n.global.t('pages.gpsTrackerProvisioning.filters.numberPlate')"
          v-model="form.number_plate"
          :items="numberPlateOptions"
          clearable
        />
        <div>
          <div class="filter-section-label">{{ i18n.global.t('pages.devices.status') }}</div>
          <v-radio-group v-model="form.status" class="radio-group-row">
            <v-radio
              value="only-active"
              :label="i18n.global.t('pages.common.filter.status.onlyActive')"
              color="blue"
            />
            <v-radio
              value="all"
              :label="i18n.global.t('pages.common.filter.status.all')"
              color="blue"
            />
          </v-radio-group>
        </div>
        <div>
          <div class="filter-section-label">{{ i18n.global.t('pages.gpsTrackerProvisioning.columns.provisioningStatus') }}</div>
          <!-- Vuetify のラベルはクリックを stopPropagation するので、label 要素を自分で置いて for で繋ぐ -->
          <div
            v-for="category in PROVISIONING_CATEGORIES"
            :key="category"
            class="d-flex align-center"
          >
            <v-checkbox
              :id="`provisioning-category-${category}`"
              v-model="form.categories"
              :value="category"
              class="flex-grow-0"
              color="blue"
              density="compact"
              hide-details
            />
            <label :for="`provisioning-category-${category}`" class="category-label">
              {{ categoryLabel(category) }}
            </label>
          </div>
        </div>
      </div>
      <div class="d-flex search-buttons mt-3">
        <v-btn
          class="ma-2 exec-btn"
          :disabled="listLoading"
          :style="{ backgroundColor: listLoading ? '#B3B8BB' : '' }"
          @click="search"
        >
          {{ listLoading ? i18n.global.t('pages.common.button.loading') : i18n.global.t('pages.common.button.exec') }}
        </v-btn>
        <v-btn class="ma-2 reset-btn" :disabled="listLoading" @click="clearFilter">
          {{ i18n.global.t('pages.common.button.reset') }}
        </v-btn>
      </div>
    </div>

    <BulkProvisioningConfirmModal
      v-model="showModal"
      :deviceIds="pendingIds"
      :categoryLabel="pendingCategoryLabel"
      :force="pendingForce"
      :skipCount="pendingSkipCount"
      :runningCount="pendingRunningCount"
      @confirm="execute"
    />
  </div>
</template>

<style scoped>
.content {
  min-width: 0;
}
.notice-label {
  font-size: 0.8rem;
  color: #666;
}
.device-list-link {
  text-decoration: underline;
  color: #1e88e5;
  font-weight: 600;
}
/* 完了して disabled になっても緑ラベルを見せる */
.refresh-btn--done.v-btn--disabled {
  opacity: 1 !important;
  color: #2e7d32 !important;
  border-color: #66bb6a !important;
}
/* リスト更新中のスピナーオーバーレイ */
.prov-loading {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.6);
}
.filter-panel {
  width: 320px;
  flex-shrink: 0;
  border-left: 1px solid #e0e0e0;
  background-color: #fff;
}
.category-label {
  cursor: pointer;
  flex: 1;
  opacity: var(--v-medium-emphasis-opacity);
}
.filter-section-label {
  font-size: 13px;
  color: gray;
  font-weight: bold;
  margin-top: 8px;
}
.prov-list {
  position: relative;
  max-height: 55vh;
  overflow: auto;
}
.prov-list th {
  min-width: 7em;
  background-color: #f8f9fa;
  position: sticky;
  top: 0;
  z-index: 1;
  white-space: nowrap;
}
.prov-list td {
  white-space: nowrap;
}
.prov-list th.checkbox-column,
.prov-list td.checkbox-column {
  min-width: 0;
  width: 1px;
  text-align: center;
  padding-left: 6px !important;
  padding-right: 6px !important;
}
.status-chip {
  display: inline-block;
  color: #fff;
  font-size: 0.75rem;
  padding: 2px 12px;
  border-radius: 12px;
  white-space: nowrap;
}
.text-muted {
  color: #aaa;
}
.force-rerun-item {
  color: #c62828;
  font-weight: 600;
}
.action-menu-btn {
  min-width: 9em;
}
.prov-list th.action-column,
.prov-list td.action-column {
  min-width: 11em;
  width: 11em;
}
</style>
